// Configuração das camadas vetoriais (Sprint 1: Biomas + UC)
// Lê variáveis do .env central (VITE_*) para montar as URLs de PMTiles.

export type VectorLayerConfig = {
  id: string;
  name: string;
  styleKey?: string;
  provider: 'pmtiles' | 'mvt';
  url: string; // URL completa para o artefato (pmtiles ou XYZ)
  minZoom: number;
  maxZoom: number;
  zIndex?: number;
  geometryType?: 'point' | 'line' | 'polygon';
  style?: {
    color?: string;
    weight?: number;
    fillColor?: string;
    fillOpacity?: number;
    radius?: number;
    strokeColor?: string;
    strokeWidth?: number;
    strokeOpacity?: number;
  };
  popupFields?: string[];
  sourceLayer?: string; // Nome da layer interna no PMTiles (tippecanoe --named-layer)
};

export type VectorLayerCatalogItem = {
  layer_id: string;
  title: string;
  storage_subpath?: string;
  blob_name?: string;
  blob_url?: string;
  source_layer?: string;
  min_zoom?: number;
  max_zoom?: number;
  popup_fields?: string[];
};

const EXCLUDED_DYNAMIC_LAYER_IDS = new Set(['estados', 'municipios']);

function env(name: string, fallback?: string): string {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const val = (import.meta as any).env?.[name];
  if (val && typeof val === 'string') return val;
  if (fallback !== undefined) return fallback;
  throw new Error(`Variável de ambiente ${name} não definida`);
}

export function getVectorLayerStyleKey(layerId: string): string {
  return String(layerId || '')
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

export function getVectorLayersConfig(): VectorLayerConfig[] {
  const baseUrl = env('VITE_TILE_BASE_URL');
  const provider = env('VITE_TILE_PROVIDER', 'pmtiles') as 'pmtiles' | 'mvt';
  const sas = ((): string => {
    try {
      return env('VITE_TILE_SAS', '');
    } catch {
      return '';
    }
  })();

  // Observação: para PMTiles com MapLibre, usaremos url no formato pmtiles://<http(s)://.../file.pmtiles>
  // O "sourceLayer" deve bater com o parâmetro --named-layer usado no tippecanoe

  // Helper para montar url
  const withSas = (u: string) => (sas ? `${u}${sas.startsWith('?') ? sas : `?${sas}`}` : u);
  const url = (subpath: string, id: string) => withSas(`${baseUrl}/${subpath}/${id}.pmtiles`);

  const layers: VectorLayerConfig[] = [
    // 1) Biomas
    {
      id: 'biomas',
      name: 'Biomas',
      provider,
      url: url('tiles/biomas', 'biomas'),
      minZoom: 0,
      maxZoom: 11,
      zIndex: 6,
      style: { color: '#2E7D32', weight: 1, fillColor: '#A5D6A7', fillOpacity: 0.4 },
      popupFields: ['bioma'],
      sourceLayer: 'biomas',
    },
    // 2) UCs
    {
      id: 'uc',
      name: 'Unidades de Conservação',
      provider,
      url: url('tiles/uc', 'uc'),
      minZoom: 3,
      maxZoom: 13,
      zIndex: 6,
      style: { color: '#1565C0', weight: 1, fillColor: '#90CAF9', fillOpacity: 0.35 },
      popupFields: ['nome_uc', 'esfera'],
      sourceLayer: 'uc',
    },
    // 3) Aldeias
    {
      id: 'aldeias',
      name: 'Aldeias',
      provider,
      url: url('tiles/aldeias', 'aldeias'),
      minZoom: 5,
      maxZoom: 14,
      zIndex: 6,
      geometryType: 'point',
      style: {
        color: '#6D4C41',
        weight: 1,
        fillColor: '#8D6E63',
        fillOpacity: 0.95,
        radius: 5,
        strokeColor: '#4E342E',
        strokeWidth: 1,
        strokeOpacity: 1,
      },
      popupFields: ['nome_aldei'],
      sourceLayer: 'aldeias',
    },
    // 4) Assentamentos
    {
      id: 'assentamentos',
      name: 'Assentamentos',
      provider,
      url: url('tiles/assentamentos', 'assentamentos'),
      minZoom: 5,
      maxZoom: 14,
      zIndex: 6,
      style: { color: '#4E342E', weight: 1, fillColor: '#D7CCC8', fillOpacity: 0.35 },
      popupFields: ['nome_proje'],
      sourceLayer: 'assentamentos',
    },
    // 5) Embargos IBAMA
    {
      id: 'embargos_ibama',
      name: 'Embargos IBAMA',
      provider,
      url: url('tiles/embargos_ibama', 'embargos_ibama'),
      minZoom: 5,
      maxZoom: 14,
      zIndex: 7,
      style: { color: '#B71C1C', weight: 1, fillColor: '#EF9A9A', fillOpacity: 0.35 },
      popupFields: ['nome_imove'],
      sourceLayer: 'embargos_ibama',
    },
    // 6) Florestas
    {
      id: 'florestas',
      name: 'Florestas',
      provider,
      url: url('tiles/florestas', 'florestas'),
      minZoom: 4,
      maxZoom: 12,
      zIndex: 5,
      style: { color: '#1B5E20', weight: 1, fillColor: '#C8E6C9', fillOpacity: 0.35 },
      popupFields: ['nome', 'governo'],
      sourceLayer: 'florestas',
    },
    // 7) Mineração
    {
      id: 'mineracao',
      name: 'Mineração',
      provider,
      url: url('tiles/mineracao', 'mineracao'),
      minZoom: 5,
      maxZoom: 13,
      zIndex: 7,
      style: { color: '#FF8F00', weight: 1, fillColor: '#FFE0B2', fillOpacity: 0.35 },
      popupFields: ['fase', 'ano'],
      sourceLayer: 'mineracao',
    },
    // 8) Quilombolas
    {
      id: 'quilombolas',
      name: 'Quilombolas',
      provider,
      url: url('tiles/quilombolas', 'quilombolas'),
      minZoom: 4,
      maxZoom: 12,
      zIndex: 6,
      style: { color: '#4A148C', weight: 1, fillColor: '#E1BEE7', fillOpacity: 0.35 },
      popupFields: ['name', 'source'],
      sourceLayer: 'quilombolas',
    },
    // 9) Terras Indígenas
    {
      id: 'terras_indigenas',
      name: 'Terras Indígenas',
      provider,
      url: url('tiles/terras_indigenas', 'terras_indigenas'),
      minZoom: 4,
      maxZoom: 12,
      zIndex: 6,
      style: { color: '#BF360C', weight: 1, fillColor: '#FFCCBC', fillOpacity: 0.35 },
      popupFields: ['terrai_nom', 'fase_ti'],
      sourceLayer: 'terras_indigenas',
    },
    // 10) PRA
    {
      id: 'pra',
      name: 'PRA',
      provider,
      url: url('tiles/pra', 'pra'),
      minZoom: 5,
      maxZoom: 14,
      zIndex: 6,
      style: { color: '#00695C', weight: 1, fillColor: '#80CBC4', fillOpacity: 0.35 },
      popupFields: ['processo'],
      sourceLayer: 'pra',
    },
    // 11) Curso d'Água
    {
      id: 'curso_dagua',
      name: "Curso d'Água",
      provider,
      url: url('tiles/curso_dagua', 'curso_dagua'),
      minZoom: 4,
      maxZoom: 16,
      zIndex: 7,
      style: { color: '#0D47A1', weight: 1, fillColor: '#BBDEFB', fillOpacity: 0.25 },
      popupFields: ['cocursodag'],
      sourceLayer: 'curso_dagua',
    },
    // 12) Curso d'Água - Trechos
    {
      id: 'trecho_curso_dagua',
      name: "Curso d'Água - Trechos",
      provider,
      url: url('tiles/trecho_curso_dagua', 'trecho_curso_dagua'),
      minZoom: 4,
      maxZoom: 16,
      zIndex: 7,
      style: { color: '#1976D2', weight: 1.2, fillColor: '#90CAF9', fillOpacity: 0.15 },
      popupFields: ['tcd_cd', 'tcd_gm_lin'],
      sourceLayer: 'trecho_curso_dagua',
    },
    // 13) Massa d'Água
    {
      id: 'massa_dagua',
      name: "Massa d'Água",
      provider,
      url: url('tiles/massa_dagua', 'massa_dagua'),
      minZoom: 4,
      maxZoom: 16,
      zIndex: 5,
      style: { color: '#01579B', weight: 1, fillColor: '#4FC3F7', fillOpacity: 0.35 },
      popupFields: ['nmoriginal', 'usoprinc'],
      sourceLayer: 'massa_dagua',
    },
    // 14) Rios
    {
      id: 'rio',
      name: 'Rios',
      provider,
      url: url('tiles/rio', 'rio'),
      minZoom: 4,
      maxZoom: 16,
      zIndex: 7,
      style: { color: '#0288D1', weight: 1.3, fillColor: '#B3E5FC', fillOpacity: 0.2 },
      popupFields: ['noriocomp', 'corio'],
      sourceLayer: 'rio',
    },
    // 15) Auto de Infração ICMBio (snapshot storage 2026-05-26)
    {
      id: 'auto_infracao_icmbio',
      name: 'Auto de Infração ICMBio',
      provider,
      url: url('tiles/auto_infracao_icmbio', 'auto_infracao_icmbio'),
      minZoom: 5,
      maxZoom: 14,
      zIndex: 7,
      geometryType: 'point',
      style: {
        color: '#E65100',
        weight: 1,
        fillColor: '#FFB74D',
        fillOpacity: 0.4,
        radius: 5,
        strokeColor: '#BF360C',
        strokeWidth: 1,
        strokeOpacity: 1,
      },
      popupFields: [],
      sourceLayer: 'auto_infracao_icmbio',
    },
    // 16) Embargos ICMBio (snapshot storage 2026-05-26)
    {
      id: 'embargos_icmbio',
      name: 'Embargos ICMBio',
      provider,
      url: url('tiles/embargos_icmbio', 'embargos_icmbio'),
      minZoom: 5,
      maxZoom: 14,
      zIndex: 7,
      geometryType: 'point',
      style: {
        color: '#33691E',
        weight: 1,
        fillColor: '#9CCC65',
        fillOpacity: 0.3,
        radius: 5,
        strokeColor: '#1B5E20',
        strokeWidth: 1,
        strokeOpacity: 1,
      },
      popupFields: [],
      sourceLayer: 'embargos_icmbio',
    },
    // 17) RPPN (snapshot storage 2026-05-26)
    {
      id: 'rppn',
      name: 'RPPN',
      provider,
      url: url('tiles/rppn', 'rppn'),
      minZoom: 4,
      maxZoom: 14,
      zIndex: 6,
      style: { color: '#2E7D32', weight: 1, fillColor: '#A5D6A7', fillOpacity: 0.3 },
      popupFields: [],
      sourceLayer: 'rppn',
    },
  ];

  return layers.map((layer) => ({
    ...layer,
    styleKey: layer.styleKey || getVectorLayerStyleKey(layer.id),
  }));
}

function buildUrlFromBlobName(blobName?: string): string {
  const normalizedBlobName = String(blobName || '').replace(/^\/+/, '');
  if (!normalizedBlobName) return '';

  const baseUrl = env('VITE_TILE_BASE_URL');
  const sas = ((): string => {
    try {
      return env('VITE_TILE_SAS', '');
    } catch {
      return '';
    }
  })();

  const base = `${baseUrl.replace(/\/+$/, '')}/${normalizedBlobName}`;
  return sas ? `${base}${sas.startsWith('?') ? sas : `?${sas}`}` : base;
}

function getDefaultDynamicStyle(layerId: string): NonNullable<VectorLayerConfig['style']> {
  const normalized = String(layerId || '').toLowerCase();
  if (normalized.includes('embargo')) {
    return {
      color: '#B71C1C',
      weight: 1,
      fillColor: '#EF9A9A',
      fillOpacity: 0.35,
      radius: 5,
      strokeColor: '#7F0000',
      strokeWidth: 1,
      strokeOpacity: 1,
    };
  }
  if (normalized.includes('infracao')) {
    return {
      color: '#E65100',
      weight: 1,
      fillColor: '#FFB74D',
      fillOpacity: 0.4,
      radius: 5,
      strokeColor: '#BF360C',
      strokeWidth: 1,
      strokeOpacity: 1,
    };
  }
  if (normalized.includes('icmbio')) {
    return {
      color: '#33691E',
      weight: 1,
      fillColor: '#9CCC65',
      fillOpacity: 0.3,
      radius: 5,
      strokeColor: '#1B5E20',
      strokeWidth: 1,
      strokeOpacity: 1,
    };
  }
  return {
    color: '#455A64',
    weight: 1,
    fillColor: '#B0BEC5',
    fillOpacity: 0.3,
    radius: 5,
    strokeColor: '#263238',
    strokeWidth: 1,
    strokeOpacity: 1,
  };
}

function inferGeometryType(layerId: string): VectorLayerConfig['geometryType'] {
  const normalized = String(layerId || '').toLowerCase();
  if (
    normalized === 'aldeias' ||
    normalized === 'auto_infracao_icmbio' ||
    normalized === 'embargos_icmbio'
  ) {
    return 'point';
  }
  return 'polygon';
}

export function getVectorLayersFromCatalog(items: VectorLayerCatalogItem[]): VectorLayerConfig[] {
  const staticLayers = getVectorLayersConfig();
  const staticById = new Map(staticLayers.map((layer) => [layer.id, layer]));
  const resolvedLayers: VectorLayerConfig[] = [];

  items.forEach((item) => {
    const layerId = String(item.layer_id || '').trim();
    if (!layerId) return;
    if (EXCLUDED_DYNAMIC_LAYER_IDS.has(layerId.toLowerCase())) return;

    const staticLayer = staticById.get(layerId);
    const url = item.blob_name
      ? buildUrlFromBlobName(item.blob_name)
      : String(item.blob_url || staticLayer?.url || '');

    if (!url) return;

    resolvedLayers.push({
      id: layerId,
      name: String(item.title || staticLayer?.name || layerId),
      styleKey: staticLayer?.styleKey || getVectorLayerStyleKey(layerId),
      provider: staticLayer?.provider || 'pmtiles',
      url,
      minZoom: typeof item.min_zoom === 'number' ? item.min_zoom : (staticLayer?.minZoom ?? 0),
      maxZoom: typeof item.max_zoom === 'number' ? item.max_zoom : (staticLayer?.maxZoom ?? 14),
      zIndex: staticLayer?.zIndex ?? 6,
      geometryType: staticLayer?.geometryType || inferGeometryType(layerId),
      style: staticLayer?.style || getDefaultDynamicStyle(layerId),
      popupFields:
        Array.isArray(item.popup_fields) && item.popup_fields.length > 0
          ? item.popup_fields
          : staticLayer?.popupFields || [],
      sourceLayer: String(item.source_layer || staticLayer?.sourceLayer || layerId),
    });
  });

  return resolvedLayers;
}
